import "./store-selector.css";

import EventEmitter from "eventemitter3";
import { Modal } from "../modal";
import { Data } from "../../../models/data";
import { MapProvider, Modals } from "../../../types/enums";
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";
import { DataLayerService } from "../../../services/data-layer-service";
import { ConfiguredMapProvider } from "../../../services/maps/configured-map-provider";
import { IStoreLocation } from "../../../models/data.interface";
import { StoreLocatorMap } from "./StoreLocatorMap/store-locator-map";
import { StoreCardList } from "./StoreCardList/store-card-list";
import { AddressSearchBox } from "./AddressSearchBox/address-search-box";
import { Util } from "../../../utils/util";
import { getCountryCentroid } from "../../../services/geo-search/country-centroid";
import { SimpleCoords } from "../../../types/geolocation-types";
import { LocationSelector } from "../LocationSelector/location-selector";
import { ILocationSelector } from "../LocationSelector/location-selector.interface";
import { StoreSelectorUtil } from "./store-selector-util";

export class StoreSelector extends Modal {
    private readonly _data: Data;

    private _listContainer: HTMLElement | null = null;
    private _searchContainer: HTMLElement | null = null;
    private _mapContainer: HTMLElement | null = null;
    private _resolve: ((selectedStore: IStoreLocation | null) => void) | null = null;

    private readonly _defaultMapZoom = 10;
    private readonly _stores: IStoreLocation[] = [];

    private _storeLocatorMap: StoreLocatorMap | null = null;
    private _storeList: StoreCardList | null = null;
    private _addressSearchBox: AddressSearchBox | null = null;
    private _componentsLoaded: boolean = false;
    private readonly _configuredMapProvider: ConfiguredMapProvider;
    private readonly _locationSelectorDialog: LocationSelector | null = null;

    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        dataLayer: DataLayerService,
        locationSelectorDialog: LocationSelector | null,
    ) {
        super(
            "modal_store_selector",
            eventBus,
            context,
            dataLayer,
            localizationService,
            { hasCloseButton: true, ariaLabel: "Store Selector" },
            Modals.STORE_SELECTOR,
        );
        this._data = data;
        this._stores = this._data.locations;
        this._locationSelectorDialog = locationSelectorDialog;
        this._configuredMapProvider = new ConfiguredMapProvider(data, context, MapProvider.GOOGLE);
    }

    public override init(): void {
        super.init();
        this._listContainer = document.getElementById("div_store_selector_list");
        this._searchContainer = document.getElementById("div_store_selector_autocomplete_container");
        this._mapContainer = document.getElementById("div_store_selector_map");
        this._initializeStoreCardList();
        this._initializeStoreLocatorMap();
        this._initializeAddressSearchBox();
        this._setEventListeners();
    }

    private _initializeStoreCardList(): void {
        if (!this._listContainer) return;

        this._storeList = new StoreCardList(this._listContainer);
        this._storeList._onStoreCardClicked = this._handleStoreCardClick;
        this._storeList._onStoreSelected = this._handleStoreSelection;
    }

    private _initializeStoreLocatorMap(): void {
        this._storeLocatorMap = new StoreLocatorMap(
            this._configuredMapProvider.provider,
            this._data,
            this._localizationService,
            this._context,
        );
        this._storeLocatorMap._onMarkerClicked = this._handleMapMarkerClick;
        this._storeLocatorMap._onStoreSelected = this._handleStoreSelection;
        this._storeLocatorMap._onMapIdle = this._handleOnMapIdle;
    }

    // init() runs before the brand data lands, so setting 275 is only readable by the time the
    // modal is first opened; rebuild the locator map if its provider or key differs from the fallback.
    private _setMapProvider(): void {
        if (!this._configuredMapProvider.refresh())
            return;

        this._initializeStoreLocatorMap();
    }

    private _initializeAddressSearchBox(): void {
        if (!this._searchContainer) return;

        this._addressSearchBox = new AddressSearchBox(
            this._searchContainer,
            this._data,
            this._context,
            this._localizationService,
        );
    }

    protected _setEventListeners(): void {
        document.getElementById("btn_store_selector_close")?.addEventListener("click", this.close);
        document.getElementById("modal_store_selector_delete")?.addEventListener("click", this._handleCancel);
        this._element?.addEventListener("cancel", this._handleCancel);
    }

    public override open(): void {
        super.open();

        if (!this._componentsLoaded) {
            this._setupComponents();
        }
    }

    private async _setupComponents(): Promise<void> {
        this._setMapProvider();

        const initialCoordinates = await this._getInitialCoordinates();
        this._data.updateLocationsDistance(initialCoordinates.lat, initialCoordinates.lng);

        await this._setupStoreMap(initialCoordinates);
        this._storeList?.setStores(this._stores);
        this._setupAddressSearchBox();

        this._componentsLoaded = true;
    }

    private _setupAddressSearchBox(): void {
        if (this._storeLocatorMap?.mapInstance) {
            this._addressSearchBox?.bindAutoComplete(
                this._storeLocatorMap?.mapInstance,
                this._handleOnSelectedPlace,
            );
        }
    }

    private readonly _handleOnSelectedPlace = (coords: { lat: number; lng: number }) => {
        if (this._storeLocatorMap?.mapInstance) {
            this._data.updateLocationsDistance(coords.lat, coords.lng);
            this._storeList?.setStores(this._stores);

            this._storeLocatorMap.centerMapOnCoordinates(coords, this._defaultMapZoom);
        }
    };

    private async _setupStoreMap(center: { lat: number; lng: number }): Promise<void> {
        if (this._mapContainer && this._storeLocatorMap) {
            const zoom = this._context.getProperty("locationPermissionDenied") ? 4 : this._defaultMapZoom;

            await this._storeLocatorMap.loadMap(this._mapContainer, "store_selector_map", center, zoom);
        }
        const iconUrl = this._context.getProperty("publicStorageUrl") + "/online3/svg/location_pinpoint.svg";
        await this._storeLocatorMap?.createMarkers(this._stores, iconUrl);
    }

    private async _getInitialCoordinates(): Promise<{ lat: number; lng: number }> {
        // get last known user coordinates from cookies
        const cookiesCoords = Util.getUserLocationFromCookie();
        if (cookiesCoords) return cookiesCoords;

        // if not available, get current user coordinates from geolocation service
        const pos: SimpleCoords | null = await Util.getCurrentPositionAsync(5000);
        if (pos) {
            Util.setLocationCookie(pos.latitude, pos.longitude, 3600);
            return { lat: pos.latitude, lng: pos.longitude };
        }

        // if not available, show popup zip selector
        const coordsFromSelector = await this._getCoordsFromLocationSelector();
        if (coordsFromSelector) {
            Util.setLocationCookie(coordsFromSelector.lat, coordsFromSelector.lng, 3600);
            return coordsFromSelector;
        }

        // if not available, use default center based on brand's country
        return this._getCountryBasedCoordinates();
    }

    private async _getCoordsFromLocationSelector(): Promise<{ lat: number; lng: number } | null> {
        if (!this._locationSelectorDialog) return null;

        const locationResult: ILocationSelector | null = await this._locationSelectorDialog?.openDialog();

        if (locationResult === null) return null;

        const coords = await this._addressSearchBox?.getCoordsFromZipCodeAsync(
            locationResult?.zip || "",
            locationResult?.countryId || "",
        );
        if (coords) {
            return { lat: coords.lat, lng: coords.lng };
        }

        return null;
    }

    private _getCountryBasedCoordinates(): { lat: number; lng: number } {
        return getCountryCentroid(this._data.getBrandProperty("StoreInfo")?.country?.IsoCode);
    }

    private readonly _handleStoreCardClick = (storeKey: string): void => {
        this._storeLocatorMap?._showStoreOnMap(storeKey);
    };

    private readonly _handleMapMarkerClick = (storeKey: string): void => {
        this._storeList?.highlightStore(storeKey);
    };

    private readonly _handleOnMapIdle = (): void => {
        if (!this._storeLocatorMap) return;

        const visibleStoreKeys = this._storeLocatorMap.getVisibleStoreKeys();
        this._storeList?.updateStoresVisibility(visibleStoreKeys);
        this._storeLocatorMap.adjustZoomIfNoStoresVisible();
    };

    public openDialog(): Promise<IStoreLocation | null> {
        return new Promise<IStoreLocation | null>((resolve) => {
            this._resolve = resolve;
            this.open();
        });
    }

    private readonly _handleStoreSelection = (storeKey: string) => {
        const selectedStore = this._stores?.find((store) => store.storeKey === storeKey) ?? null;

        if (selectedStore && this._isStoreDistant(selectedStore)) {
            StoreSelectorUtil._showDistanceWarningDialog(
                selectedStore,
                () => {
                    this.close();
                    this._resolve?.(selectedStore);
                },
                () => {
                    // Do nothing on cancel
                },
            );
            return;
        }
        this.close();
        this._resolve?.(selectedStore);
    };

    // Called by the "Cancel" button or the dialog's cancel event
    private readonly _handleCancel = () => {
        this.close();
        this._resolve?.(null);
    };

    private _isStoreDistant(store: IStoreLocation | undefined): boolean {
        // more than 5 miles
        return (store?.distance ?? 0) > 5;
    }
}
